import React, { createContext, Key, ReactNode, useCallback, useContext, useState } from "react";
import { isEmpty } from "lodash";
import { GetInboundRemittanceApiParams, PaginatedApiParams } from "../types/reconciliation";

interface InboundRemittanceTableContextType {
  page: number;
  pageSize: number;
  ordering: string | undefined;
  dateCreation: Key[];
  userCreation: Key[];
  name: Key[];
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setOrdering: (ordering: string) => void;
  setDateCreation: (date: Key[]) => void;
  setUserCreation: (user: Key[]) => void;
  setName: (name: Key[]) => void;
  getParamsForApiRequest: (defaultFilters?: Partial<GetInboundRemittanceApiParams>) => (GetInboundRemittanceApiParams & PaginatedApiParams);
}

const InboundRemittanceTableContext = createContext<InboundRemittanceTableContextType | undefined>(undefined);

export const InboundRemittanceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [ordering, setOrdering] = useState<string | undefined>(undefined);
  const [dateCreation, setDateCreation] = useState<Key[]>([]);
  const [userCreation, setUserCreation] = useState<Key[]>([]);
  const [name, setName] = useState<Key[]>([]);

  const getParamsForApiRequest = useCallback((defaultFilters?: GetInboundRemittanceApiParams) => {
    return {
      page,
      page_size: pageSize,
      ...(!isEmpty(ordering) && { ordering: ordering }),
      ...(!isEmpty(defaultFilters) && { ...defaultFilters }),
      ...(!isEmpty(dateCreation) && {
        date_creation_min: dateCreation?.at(0) as string,
        date_creation_max: dateCreation?.at(1) as string
      }),
      ...(!isEmpty(userCreation) && { user_creation: userCreation?.join(",") }),
      ...(!isEmpty(name) && { name: name?.join(",") })
    };
  }, [dateCreation, name, ordering, page, pageSize, userCreation]);

  const value: InboundRemittanceTableContextType = {
    page,
    pageSize,
    ordering,
    dateCreation,
    userCreation,
    name,
    setPage,
    setPageSize,
    setOrdering,
    setDateCreation,
    setUserCreation,
    setName,
    getParamsForApiRequest
  };

  return (
    <InboundRemittanceTableContext.Provider value={value}>
      {children}
    </InboundRemittanceTableContext.Provider>
  );
};

export const useInboundRemittanceTable = (): InboundRemittanceTableContextType => {
  const context = useContext(InboundRemittanceTableContext);
  if (context === undefined) {
    throw new Error("useInboundRemittanceTable deve essere utilizzato all'interno di un InboundRemittanceProvider");
  }
  return context;
};